import React, {useState} from 'react';
import ReactMarkdown from 'react-markdown';
import './MultipleChoice.css';
import {FaBookmark, FaRegBookmark, FaEyeSlash} from "react-icons/fa";
import {HOST} from "../config";
import {markdownComponents} from "../markdownImage";

const progressLabels = {
    0: 'Not learned',
    1: 'Incorrect',
    2: 'Unsure',
    3: '1x Correct',
    4: '2x Correct',
    5: '3x Correct',
};

const progressMapping = {
    'untouched': 0,
    'incorrect': 1,
    'unsure': 2,
    'correct1': 3,
    'correct2': 4,
    'correct3': 5,
};

const objectFlip = (obj) => {
    const ret = {};
    Object.keys(obj).forEach(key => {
        ret[obj[key]] = key;
    });
    return ret;
};

const MultipleChoice = ({card, updateCardStatus, onNext, interactive, onHide}) => {
    // Determine the input type based on the card type
    const isSingleChoice = card.type === 'singleChoice';

    // For single choice, selected is a string; for multiple choice, it's an array
    const [selected, setSelected] = useState(isSingleChoice ? '' : []);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);
    const [bookmarked, setBookmarked] = useState(card.bookmarked || false);
    const [progress, setProgress] = useState(
        // Expect progress to be a numeric value between 0 and 3
        card.progress !== undefined ? progressMapping[card.progress] : 0
    );

    const handleChange = (e) => {
        if (isSingleChoice) {
            setSelected(e.target.value);
        } else {
            const option = e.target.value;
            if (e.target.checked) {
                setSelected(prev => [...prev, option]);
            } else {
                setSelected(prev => prev.filter(val => val !== option));
            }
        }
    };

    // Helper function: compare two arrays (order-independent)
    const arraysEqual = (a, b) => {
        if (a.length !== b.length) return false;
        const sortedA = [...a].sort();
        const sortedB = [...b].sort();
        return sortedA.every((val, idx) => val === sortedB[idx]);
    };

    const handleSubmit = React.useCallback(() => {
        // Evaluate whether the selected answer(s) are correct
        const evaluateAnswer = () => {
            const correctOptions = card.options
                .filter(opt => opt.correct)
                .map(opt => opt.label);
            if (isSingleChoice) {
                return selected === correctOptions[0];
            } else {
                if (selected.length !== correctOptions.length) return false;
                return arraysEqual(selected, correctOptions);
            }
        };

        if ((isSingleChoice && !selected) || (!isSingleChoice && selected.length === 0)) return;
        const isCorrect = evaluateAnswer();
        let res;
        if (isCorrect) {
            if (card.progress === 'correct1') {
                res = 'correct2';
            } else if (card.progress === 'correct2' || card.progress === 'correct3') {
                res = 'correct3';
            } else {
                res = 'correct1';
            }
        } else {
            res = 'incorrect'
        }
        setResult(res);
        setSubmitted(true);
    }, [card.progress, card.options, isSingleChoice, selected]);

    // Handler for Next button click
    const handleNext = React.useCallback(() => {
        // Reset internal state before calling onNext callback
        setSelected(isSingleChoice ? '' : []);
        setSubmitted(false);
        setResult(null);

        updateCardStatus(result);
        if (onNext) {
            onNext();
        }
    }, [isSingleChoice, onNext, result, updateCardStatus]);

    const toggleBookmark = () => {
        // Here you can also perform an API call to update bookmark state.
        fetch(HOST + `/api/cards/${card._id}/bookmark`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({bookmarked: !bookmarked}),
        })
            .then(response => {
                if (!response.ok) {
                    alert("Error");
                    throw new Error('Error updating bookmark status.');
                }
                return response.json();
            })
            .then(data => {
                setBookmarked(!bookmarked);
            })
            .catch(err => {
                console.error(err);
            });
    };

    const handleProgressChange = (e) => {
        // Here you can also send the new progress value to your server.
        const value = e.target.value;
        fetch(HOST + `/api/cards/${card._id}/progress`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({progress: objectFlip(progressMapping)[parseInt(value, 10)]}),
        })
            .then(response => {
                if (!response.ok) {
                    alert("Error");
                    throw new Error('Error updating progress.');
                }
                return response.json();
            })
            .then(data => {
                setProgress(parseInt(value, 10));
                console.log('Progress updated:', data);
            })
            .catch(err => {
                console.error(err);
            });
    };

    React.useEffect(() => {
        const checkKeyPress = (e) => {
            const {key, keyCode} = e;
            console.log(key, keyCode);
            if (isSingleChoice && interactive) {
                if (!submitted) {
                    if ((keyCode === 97 || keyCode === 49) && card.options[0]?.label) {
                        setSelected(card.options[0].label);
                    } else if ((keyCode === 98 || keyCode === 50) && card.options[1]?.label) {
                        setSelected(card.options[1].label);
                    } else if ((keyCode === 99 || keyCode === 51) && card.options[2]?.label) {
                        setSelected(card.options[2].label);
                    } else if ((keyCode === 100 || keyCode === 52) && card.options[3]?.label) {
                        setSelected(card.options[3].label);
                    } else if (keyCode === 13) {
                        handleSubmit();
                    }
                } else {
                    if (keyCode === 13) {
                        handleNext();
                    }
                }
            }
        }

        window.addEventListener("keydown", checkKeyPress)
        return () => {
            window.removeEventListener("keydown", checkKeyPress)
        }
    }, [card.options, submitted, interactive, handleSubmit, isSingleChoice, handleNext]);

    return (
        <div className="multiple-choice-container">
            {interactive && onHide && (
                <button className="hide-button" onClick={onHide} title="Hide this card from now on">
                    <FaEyeSlash/>
                </button>
            )}
            {!interactive && (
                <div className="card-meta">
                    <button className="bookmark-button" onClick={toggleBookmark}>
                        {bookmarked ? (
                            <FaBookmark title="Remove Bookmark"/>
                        ) : (
                            <FaRegBookmark title="Add Bookmark"/>
                        )}
                    </button>
                    <div className="progress-slider">
                        <select
                            id={`progress-${card.id}`}
                            value={progress}
                            onChange={handleProgressChange}
                        >
                            {Object.entries(progressLabels).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
            <div className="question">
                <ReactMarkdown components={markdownComponents}>{card.question}</ReactMarkdown>
            </div>
            <form className="options">
                {card.options.map((option, index) => {
                    const inputType = isSingleChoice ? "radio" : "checkbox";
                    const isChecked = isSingleChoice ? (selected === option.label) : selected.includes(option.label);
                    // When submitted, add classes for styling
                    let extraClass = "";
                    if (submitted) {
                        if (option.correct) {
                            extraClass = " correct";
                        } else if (isChecked && !option.correct) {
                            extraClass = " incorrect";
                        }
                    }
                    return (
                        <label key={index} className={`option-label${extraClass}`}>
                            <input
                                type={inputType}
                                name="option"
                                value={option.label}
                                disabled={submitted || !interactive}
                                checked={isChecked}
                                onChange={handleChange}
                            />
                            {option.label}
                        </label>
                    );
                })}
            </form>
            {!submitted && interactive && (
                <button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={isSingleChoice ? !selected : selected.length === 0}
                    type="button"
                >
                    Submit
                </button>
            )}
            {submitted && (
                <div className="result">
                    {result.startsWith("correct") ? (
                        <span className="correct-msg">Correct!</span>
                    ) : (
                        <span className="incorrect-msg">
                            Incorrect. The correct answer{isSingleChoice ? " is" : "s are"}:{" "}
                            <strong>
                                {card.options
                                    .filter(opt => opt.correct)
                                    .map(opt => opt.label)
                                    .join(', ')}
                            </strong>
                        </span>
                    )}
                    {card.explanation && (
                        <div className="explanation">
                            {card.explanation}
                        </div>
                    )}
                    <button className="next-button" onClick={handleNext} type="button">
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default MultipleChoice;
